import { CheckCircle2, Circle, Minus, PencilLine, Plus, RotateCcw, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  formatPurchaseOrderPrice,
  formatPurchaseOrderQuantity,
  getPurchaseOrderQuantityStep,
} from '../../hooks/purchase-orders/purchaseOrderHelpers';
import { buildAssetUrl } from '../../services/apiConfig';
import { CustomDatePicker, CustomSelect, ModalBackdrop } from '../common';

const progressStatuses = ['Initial', 'In Progress', 'Received', 'Completed'];
const actualPriceRequiredMessage = 'Enter a valid actual price.';
const requestedQuantityRequiredMessage = 'Enter the quantity that was actually received.';
const sacksQuantityWholeNumberMessage = 'Sacks quantity must be a whole number.';
const lansaNameRequiredMessage = 'Lansa name is required before receiving can proceed.';
const warehouseLocationRequiredMessage =
  'Warehouse location is required before receiving can proceed.';
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function PurchaseOrderProgressModal({
  purchaseOrder,
  values,
  lansaOptions = [],
  warehouseLocationOptions = [],
  receivingEditValues,
  initialReceivingEditValues,
  isSaving,
  isReceivingSaving,
  error,
  hasPaidBalancing = false,
  hasSavedBalancing = false,
  onChange,
  onReceivingChange,
  onClose,
  onResetProgress,
  onResetReceiving,
  onFinalizeForBalancing,
  onReopenReceiving,
  onSubmit,
}) {
  const currentStatus = purchaseOrder.status;
  const isCancelled = currentStatus === 'Cancelled';
  const currentIndex = isCancelled
    ? -1
    : progressStatuses.indexOf(
        progressStatuses.includes(currentStatus) ? currentStatus : 'Initial'
      );

  const canUpdateInProgress = ['Initial', 'In Progress'].includes(currentStatus) && !isCancelled;
  const canComplete = currentStatus === 'Received' && !isCancelled;
  const normalizedRequestedQuantity = Number(purchaseOrder.quantity);
  const normalizedReceivedQuantity = Number(values.receivedQuantity || purchaseOrder.quantity);
  const hasDiscrepancy =
    Number.isFinite(normalizedReceivedQuantity) &&
    normalizedReceivedQuantity !== normalizedRequestedQuantity;
  const normalizedInitialPrice = Number(purchaseOrder.price);
  const normalizedActualPrice = Number(values.actualPrice || purchaseOrder.price);
  const hasPriceDiscrepancy =
    Number.isFinite(normalizedInitialPrice) &&
    Number.isFinite(normalizedActualPrice) &&
    normalizedActualPrice !== normalizedInitialPrice;
  const currentStepLabel = getProgressStepLabel(currentStatus);
  const requestedQuantityValue = formatPurchaseOrderQuantity(
    values.receivedQuantity || purchaseOrder.quantity,
    purchaseOrder.stockUnit
  );
  const initialStageTwoValues = getInitialStageTwoValues(purchaseOrder);
  const hasUnsavedProgressChanges =
    values.receivedQuantity !== initialStageTwoValues.receivedQuantity ||
    values.actualPrice !== initialStageTwoValues.actualPrice ||
    values.lansaName !== initialStageTwoValues.lansaName ||
    values.warehouseLocation !== initialStageTwoValues.warehouseLocation ||
    values.deliveryDate !== initialStageTwoValues.deliveryDate;
  const hasUnsavedReceivingChanges = hasChangedReceivingValues(
    receivingEditValues,
    initialReceivingEditValues || []
  );
  const hasStaffReceivingDraft =
    currentStatus === 'Initial' && hasReceivingDraftItems(purchaseOrder.receivingItems);
  const requestedQuantityError =
    error === requestedQuantityRequiredMessage || error === sacksQuantityWholeNumberMessage
      ? error
      : '';
  const actualPriceError = error === actualPriceRequiredMessage ? actualPriceRequiredMessage : '';
  const lansaNameError = error === lansaNameRequiredMessage ? lansaNameRequiredMessage : '';
  const warehouseLocationError =
    error === warehouseLocationRequiredMessage ? warehouseLocationRequiredMessage : '';
  const [isActualPriceEditing, setIsActualPriceEditing] = useState(false);
  const [isRequestedQuantityEditing, setIsRequestedQuantityEditing] = useState(false);
  const [isLansaNameEditing, setIsLansaNameEditing] = useState(false);
  const [isWarehouseLocationEditing, setIsWarehouseLocationEditing] = useState(false);
  const [isDeliveryDateEditing, setIsDeliveryDateEditing] = useState(false);
  const requestedQuantityInputRef = useRef(null);
  const actualPriceInputRef = useRef(null);
  const lansaNameSelectRef = useRef(null);
  const warehouseLocationSelectRef = useRef(null);

  useEffect(() => {
    setIsActualPriceEditing(false);
    setIsRequestedQuantityEditing(false);
    setIsLansaNameEditing(false);
    setIsWarehouseLocationEditing(false);
    setIsDeliveryDateEditing(false);
  }, [purchaseOrder.id, purchaseOrder.status]);

  useEffect(() => {
    if (isRequestedQuantityEditing) {
      focusEditableField(requestedQuantityInputRef);
    }
  }, [isRequestedQuantityEditing]);

  useEffect(() => {
    if (isActualPriceEditing) {
      focusEditableField(actualPriceInputRef);
    }
  }, [isActualPriceEditing]);

  useEffect(() => {
    if (isLansaNameEditing) {
      openEditableSelect(lansaNameSelectRef);
    }
  }, [isLansaNameEditing]);

  useEffect(() => {
    if (isWarehouseLocationEditing) {
      openEditableSelect(warehouseLocationSelectRef);
    }
  }, [isWarehouseLocationEditing]);

  useEffect(() => {
    if (requestedQuantityError) {
      handleRequestedQuantityToggle(true);
      return;
    }

    if (actualPriceError) {
      handleActualPriceToggle(true);
      return;
    }

    if (lansaNameError) {
      handleLansaNameToggle(true);
      return;
    }

    if (warehouseLocationError) {
      handleWarehouseLocationToggle(true);
    }
  }, [actualPriceError, lansaNameError, requestedQuantityError, warehouseLocationError]);

  function handleRequestedQuantityToggle(nextIsEditing) {
    setIsRequestedQuantityEditing(nextIsEditing);

    if (nextIsEditing) {
      setIsActualPriceEditing(false);
      setIsLansaNameEditing(false);
      setIsWarehouseLocationEditing(false);
      setIsDeliveryDateEditing(false);
    }
  }

  function handleActualPriceToggle(nextIsEditing) {
    setIsActualPriceEditing(nextIsEditing);

    if (nextIsEditing) {
      setIsRequestedQuantityEditing(false);
      setIsLansaNameEditing(false);
      setIsWarehouseLocationEditing(false);
      setIsDeliveryDateEditing(false);
    }
  }

  function handleLansaNameToggle(nextIsEditing) {
    setIsLansaNameEditing(nextIsEditing);

    if (nextIsEditing) {
      setIsRequestedQuantityEditing(false);
      setIsActualPriceEditing(false);
      setIsWarehouseLocationEditing(false);
      setIsDeliveryDateEditing(false);
    }
  }

  function handleWarehouseLocationToggle(nextIsEditing) {
    setIsWarehouseLocationEditing(nextIsEditing);

    if (nextIsEditing) {
      setIsRequestedQuantityEditing(false);
      setIsActualPriceEditing(false);
      setIsLansaNameEditing(false);
      setIsDeliveryDateEditing(false);
    }
  }

  function handleDeliveryDateToggle(nextIsEditing) {
    setIsDeliveryDateEditing(nextIsEditing);

    if (nextIsEditing) {
      setIsRequestedQuantityEditing(false);
      setIsActualPriceEditing(false);
      setIsLansaNameEditing(false);
      setIsWarehouseLocationEditing(false);
    }
  }

  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.28)]"
      className="px-3 py-3 sm:px-4"
      contentClassName="block !min-h-0 !px-0 !py-0"
    >
      <div className="mx-auto w-full max-w-7xl">
        <div className="mx-auto mb-2 grid max-w-7xl gap-2 xl:grid-cols-3">
          <div className="hidden xl:block" />
          <div className="flex justify-center">
            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--theme-border-soft)] bg-white text-[#6e6257] shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.05)] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              data-progress-action="true"
              onClick={onClose}
              disabled={isSaving}
              aria-label="Close purchase order progress modal"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          <div className="hidden xl:block" />
        </div>

        <div className="mx-auto grid max-w-7xl gap-2.5 md:grid-cols-3">
            <StepCard
              stepNumber="1"
              title="Initial"
              isCurrent={currentIndex === 0}
              isReached={currentIndex >= 0}
              indicatorLabel={currentStepLabel}
            >
              {hasStaffReceivingDraft ? (
                <span className="relative mb-2 inline-flex w-fit items-center gap-2 rounded-full border border-[#a8d3ad] bg-[#edf8ef] px-3 py-1 text-[0.72rem] font-semibold text-[#2f7a3f]">
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Staff receiving draft saved
                  <span className="absolute -right-1.5 -top-1.5 flex h-3 w-3" aria-hidden="true">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#e03f3f] opacity-75" />
                    <span className="relative inline-flex h-3 w-3 rounded-full border border-white bg-[#e03f3f]" />
                  </span>
                </span>
              ) : null}
              <div className="grid gap-1.5">
                <DetailCard label="P.O. Code" value={formatPurchaseOrderCode(purchaseOrder)} />
                <DetailCard
                  label="Supplier"
                  value={formatSupplierDisplay(purchaseOrder.sacksMark, purchaseOrder.supplierName)}
                />
                <DetailCard
                  label="Requested Quantity"
                  value={formatPurchaseOrderQuantity(
                    purchaseOrder.quantity,
                    purchaseOrder.stockUnit
                  )}
                />
                <DetailCard
                  label="Initial Price per Kilo"
                  value={formatPurchaseOrderPrice(purchaseOrder.price)}
                />
                <DetailCard label="Warehouse Location" value={purchaseOrder.warehouseLocation || '-'} />
                <DetailCard label="Lansa Name" value={purchaseOrder.lansaName || '-'} />
                <DetailCard label="Delivery Date" value={formatDate(purchaseOrder.deliveryDate)} />
              </div>
            </StepCard>

            <StepCard
              stepNumber="2"
              title="In Progress"
              isCurrent={currentIndex === 1}
              isReached={currentIndex >= 1}
              indicatorLabel={currentStepLabel}
              headerAction={
                canUpdateInProgress && hasUnsavedProgressChanges ? (
                  <button
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[#e3d9cf] bg-white text-[#8a7b6d] transition hover:border-[#d3c5b8] hover:bg-[var(--theme-surface-soft)]"
                    type="button"
                    data-progress-action="true"
                    onClick={() => {
                      onResetProgress?.();
                      setIsActualPriceEditing(false);
                      setIsRequestedQuantityEditing(false);
                      setIsLansaNameEditing(false);
                      setIsWarehouseLocationEditing(false);
                      setIsDeliveryDateEditing(false);
                    }}
                    aria-label="Reset unsaved progress edits"
                    title="Reset unsaved progress edits"
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                ) : null
              }
            >
              <div className="grid gap-1.5">
                <EditableDetailCard
                  label="Requested Quantity"
                  value={requestedQuantityValue}
                  isEditing={isRequestedQuantityEditing}
                  onToggle={handleRequestedQuantityToggle}
                  canEdit={canUpdateInProgress}
                  error={requestedQuantityError}
                >
                  <label className="grid gap-1.5 border-t border-[#f1e9e1] pt-2">
                    <span className="text-[0.76rem] font-medium text-[var(--theme-text-subtle)]">Requested Quantity</span>
                    <input
                      ref={requestedQuantityInputRef}
                      className={`min-h-[38px] rounded-[12px] border bg-white px-3 py-2 text-[0.84rem] outline-none transition ${
                        requestedQuantityError
                          ? 'border-[#d78b7d] focus:border-[#c76552]'
                          : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
                      }`}
                      type="number"
                      name="receivedQuantity"
                      value={values.receivedQuantity}
                      onChange={onChange}
                      min="0"
                      step={getPurchaseOrderQuantityStep(purchaseOrder.stockUnit)}
                      inputMode="decimal"
                    />
                  </label>
                </EditableDetailCard>
                <EditableDetailCard
                  label="Actual Price Per KG"
                  value={formatPurchaseOrderPrice(values.actualPrice || purchaseOrder.price)}
                  isEditing={isActualPriceEditing}
                  onToggle={handleActualPriceToggle}
                  canEdit={canUpdateInProgress}
                  error={actualPriceError}
                >
                  <label className="grid gap-1.5 border-t border-[#f1e9e1] pt-2">
                    <span className="text-[0.76rem] font-medium text-[var(--theme-text-subtle)]">Actual Price Per KG</span>
                    <input
                      ref={actualPriceInputRef}
                      className={`min-h-[38px] rounded-[12px] border bg-white px-3 py-2 text-[0.84rem] outline-none transition ${
                        actualPriceError
                          ? 'border-[#d78b7d] focus:border-[#c76552]'
                          : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
                      }`}
                      type="number"
                      name="actualPrice"
                      value={values.actualPrice}
                      onChange={onChange}
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                    />
                  </label>
                </EditableDetailCard>
                <EditableDetailCard
                  label="Lansa Name"
                  value={values.lansaName || '-'}
                  isEditing={isLansaNameEditing}
                  onToggle={handleLansaNameToggle}
                  canEdit={canUpdateInProgress}
                  error={lansaNameError}
                >
                  <div className="border-t border-[#f1e9e1] pt-2">
                    <CustomSelect
                      ref={lansaNameSelectRef}
                      label="Lansa Name"
                      name="lansaName"
                      value={values.lansaName}
                      onChange={onChange}
                      options={lansaOptions}
                      placeholder="Select lansa"
                      error={lansaNameError}
                      errorClassName="hidden"
                    />
                  </div>
                </EditableDetailCard>
                <EditableDetailCard
                  label="Delivery Date"
                  value={formatDate(values.deliveryDate)}
                  isEditing={isDeliveryDateEditing}
                  onToggle={handleDeliveryDateToggle}
                  canEdit={canUpdateInProgress}
                >
                  <div className="border-t border-[#f1e9e1] pt-2">
                    <CustomDatePicker
                      label="Delivery Date"
                      name="deliveryDate"
                      value={values.deliveryDate || ''}
                      onChange={onChange}
                      triggerShapeClassName="rounded-[12px]"
                    />
                  </div>
                </EditableDetailCard>
                <EditableDetailCard
                  label="Warehouse Location"
                  value={values.warehouseLocation || '-'}
                  isEditing={isWarehouseLocationEditing}
                  onToggle={handleWarehouseLocationToggle}
                  canEdit={canUpdateInProgress}
                  error={warehouseLocationError}
                >
                  <div className="border-t border-[#f1e9e1] pt-2">
                    <CustomSelect
                      ref={warehouseLocationSelectRef}
                      label="Warehouse Location"
                      name="warehouseLocation"
                      value={values.warehouseLocation}
                      onChange={onChange}
                      options={warehouseLocationOptions}
                      placeholder="Select warehouse"
                      error={warehouseLocationError}
                      errorClassName="hidden"
                    />
                  </div>
                </EditableDetailCard>
              </div>
              {hasDiscrepancy ? (
                <span className="mt-2 inline-flex items-center gap-2 rounded-[10px] border border-[#ead7a8] bg-[#fff8e6] px-2.5 py-1.5 text-[0.74rem] font-semibold text-[#8a6424]">
                  <span className="h-2 w-2 rounded-full bg-[#d99516]" aria-hidden="true" />
                  Supplier released quantity differs from the initial request
                </span>
              ) : null}
              {hasPriceDiscrepancy ? (
                <span className="mt-2 inline-flex items-center gap-2 rounded-[10px] border border-[#ead7a8] bg-[#fff8e6] px-2.5 py-1.5 text-[0.74rem] font-semibold text-[#8a6424]">
                  <span className="h-2 w-2 rounded-full bg-[#d99516]" aria-hidden="true" />
                  Actual price differs from the initial price
                </span>
              ) : null}
              {canUpdateInProgress ? (
                <button
                  className="mt-3 w-full rounded-full bg-[var(--theme-accent)] px-4 py-2.5 text-[0.84rem] font-semibold text-white shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.18)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)] disabled:opacity-70 disabled:shadow-none"
                  type="button"
                  data-progress-action="true"
                  onClick={onSubmit}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Update In Progress'}
                </button>
              ) : null}
            </StepCard>

            <StepCard
              stepNumber="3"
              title="Received / Completed"
              isCurrent={currentIndex >= 2}
              isReached={currentIndex >= 2}
              indicatorLabel={currentStepLabel}
              headerAction={
                canComplete && hasUnsavedReceivingChanges ? (
                  <button
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[#e3d9cf] bg-white text-[#8a7b6d] transition hover:border-[#d3c5b8] hover:bg-[var(--theme-surface-soft)]"
                    type="button"
                    data-progress-action="true"
                    onClick={onResetReceiving}
                    aria-label="Reset unsaved receiving edits"
                    title="Reset unsaved receiving edits"
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                ) : null
              }
            >
              <FinalReviewPanel
                purchaseOrder={purchaseOrder}
                receivingEditValues={receivingEditValues}
                isReceivingSaving={isReceivingSaving}
                error={error}
                onReceivingChange={onReceivingChange}
                canComplete={canComplete}
                hasPaidBalancing={hasPaidBalancing}
                hasSavedBalancing={hasSavedBalancing}
                isSaving={isSaving}
                onFinalizeForBalancing={onFinalizeForBalancing}
                onReopenReceiving={onReopenReceiving}
                onComplete={onSubmit}
              />
            </StepCard>
        </div>

        {isCancelled ? (
          <Message tone="error">
            This purchase order is currently cancelled. Please update it from the edit form if you need to continue the delivery process.
          </Message>
        ) : null}

        {error &&
        currentIndex < 2 &&
        !requestedQuantityError &&
        !actualPriceError &&
        !lansaNameError &&
        !warehouseLocationError ? (
          <Message tone="error">{error}</Message>
        ) : null}
      </div>
    </ModalBackdrop>
  );
}

function StepCard({
  stepNumber,
  title,
  isCurrent,
  isReached,
  indicatorLabel,
  headerAction,
  children,
}) {
  return (
    <section
      className={`relative overflow-visible rounded-[14px] border p-3 ${
        isCurrent
          ? 'border-[var(--theme-accent)] bg-[linear-gradient(180deg,#ffffff_0%,var(--theme-accent-soft)_100%)] shadow-[0_12px_24px_rgba(var(--theme-shadow-rgb),0.12)] ring-1 ring-[var(--theme-accent-soft)]'
          : isReached
            ? 'border-[var(--theme-border)] bg-white shadow-[0_6px_14px_rgba(var(--theme-shadow-rgb),0.025)]'
            : 'border-[#efe7df] bg-[var(--theme-app-bg)]'
      }`}
    >
      <div
        className={`absolute bottom-1 left-[-2px] top-1 w-1.5 rounded-l-[10px] ${
          isCurrent ? 'bg-[var(--theme-accent)]' : isReached ? 'bg-[color-mix(in_srgb,var(--theme-accent)_34%,white)]' : 'bg-[var(--theme-border-soft)]'
        }`}
        aria-hidden="true"
      />
      <div className="mb-2.5 flex items-start justify-between gap-2 pl-1">
        <div className="flex min-w-0 items-center gap-2">
          <div
            className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-[0.78rem] font-semibold ${
              isCurrent
                ? 'bg-[var(--theme-accent)] text-white'
                : isReached
                  ? 'bg-[var(--theme-accent-soft)] text-[var(--theme-accent-strong)]'
                  : 'border border-[var(--theme-border)] bg-[var(--theme-surface-soft)] text-[var(--theme-accent-strong)]'
            }`}
          >
          {stepNumber}
          </div>
          <div className="flex min-w-0 items-center gap-1.5">
            {isReached ? (
              <CheckCircle2
                className={`h-3.5 w-3.5 shrink-0 ${isCurrent || isReached ? 'text-[var(--theme-accent)]' : 'text-[var(--theme-text-subtle)]'}`}
              />
            ) : (
              <Circle className="h-3.5 w-3.5 shrink-0 text-[var(--theme-border)]" />
            )}
            <h3 className="m-0 truncate text-[0.9rem] font-semibold text-[var(--theme-text)]">{title}</h3>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span
            className={`rounded-full px-2 py-0.5 text-[0.62rem] font-semibold uppercase tracking-[0.08em] ${
            isCurrent
              ? 'bg-[var(--theme-accent)] text-white shadow-[0_6px_14px_rgba(var(--theme-shadow-rgb),0.22)]'
              : isReached
                ? 'bg-[var(--theme-accent-soft)] text-[var(--theme-accent-strong)]'
                : 'bg-[var(--theme-surface-soft)] text-[#aea194]'
            }`}
          >
            {isCurrent ? 'Current' : isReached ? 'Done' : 'Pending'}
          </span>
        </div>
      </div>
      <div className="mb-2.5 flex items-center justify-between gap-2 pl-1">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={`h-2 w-2 rounded-full ${
              isCurrent
                ? 'bg-[var(--theme-accent)] shadow-[0_0_0_4px_rgba(var(--theme-shadow-rgb),0.16)]'
                : isReached
                  ? 'bg-[var(--theme-accent)]'
                  : 'bg-[#d7cec5]'
            }`}
            aria-hidden="true"
          />
          <span
            className={`text-[0.66rem] font-semibold uppercase tracking-[0.08em] ${
              isCurrent || isReached ? 'text-[var(--theme-accent-strong)]' : 'text-[var(--theme-text-muted)]'
            }`}
          >
            Current stage: {indicatorLabel}
          </span>
        </div>
        {headerAction}
      </div>
      <div className="border-t border-[#f2ebe4] pt-2.5">{children}</div>
    </section>
  );
}

function getProgressStepLabel(currentStatus) {
  if (currentStatus === 'Completed') {
    return 'Completed';
  }

  if (currentStatus === 'Received') {
    return 'Received';
  }

  if (currentStatus === 'In Progress') {
    return 'In Progress';
  }

  return 'Initial';
}

function hasReceivingDraftItems(receivingItems) {
  return Array.isArray(receivingItems) && receivingItems.length > 0;
}

function DetailCard({ label, value }) {
  return (
    <div className="rounded-[10px] border border-[#f1e9e1] bg-[linear-gradient(180deg,#ffffff_0%,var(--theme-app-bg)_100%)] px-3 py-2">
      <span className="block text-[0.64rem] uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
        {label}
      </span>
      <strong className="mt-1 block text-[0.82rem] font-medium text-[var(--theme-text)]">{value}</strong>
    </div>
  );
}

function EditableDetailCard({
  label,
  value,
  isEditing,
  onToggle,
  canEdit = true,
  error = '',
  children,
}) {
  const cardRef = useRef(null);

  useEffect(() => {
    if (!canEdit && isEditing) {
      onToggle(false);
    }
  }, [canEdit, isEditing, onToggle]);

  useEffect(() => {
    if (!canEdit || !isEditing) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (
        isProgressActionElement(event.target) ||
        isEditorToggleElement(event.target) ||
        isCustomSelectDropdownElement(event.target)
      ) {
        return;
      }

      if (!cardRef.current?.contains(event.target)) {
        onToggle(false);
      }
    }

    function handleFocusIn(event) {
      if (
        isProgressActionElement(event.target) ||
        isEditorToggleElement(event.target) ||
        isCustomSelectDropdownElement(event.target)
      ) {
        return;
      }

      if (!cardRef.current?.contains(event.target)) {
        onToggle(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('focusin', handleFocusIn);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [canEdit, isEditing, onToggle]);

  return (
    <div
      ref={cardRef}
      className={`rounded-[10px] border bg-[linear-gradient(180deg,#ffffff_0%,var(--theme-app-bg)_100%)] px-3 py-2 ${
        error
          ? 'border-[#d78b7d] shadow-[0_0_0_1px_rgba(199,75,57,0.08)]'
          : 'border-[#f1e9e1]'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="block text-[0.64rem] uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
            {label}
          </span>
          <strong className="mt-1 block text-[0.82rem] font-medium text-[var(--theme-text)]">{value}</strong>
        </div>
        {canEdit ? (
          <button
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#e3d9cf] bg-white text-[#73675d] transition hover:border-[#d3c5b8] hover:bg-[var(--theme-surface-soft)]"
            type="button"
            data-progress-editor-toggle="true"
            onClick={() => onToggle(!isEditing)}
            aria-label={isEditing ? `Hide ${label} editor` : `Edit ${label}`}
            title={isEditing ? `Hide ${label} editor` : `Edit ${label}`}
          >
            {isEditing ? (
              <X className="h-4 w-4" aria-hidden="true" />
            ) : (
              <PencilLine className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        ) : null}
      </div>
      {canEdit && isEditing ? <div className="mt-2">{children}</div> : null}
      {error ? <span className="mt-2 block text-[0.74rem] text-[#bb5745]">{error}</span> : null}
    </div>
  );
}

function CompactSummary({ label, value, detail = '', tone = 'neutral' }) {
  const toneClassName =
    tone === 'error'
      ? 'border-[#ef9f91] bg-[linear-gradient(180deg,#fffafa_0%,#fff3f0_100%)] shadow-[0_0_0_1px_rgba(199,75,57,0.08)]'
      : tone === 'warning'
        ? 'border-[#e7bd64] bg-[linear-gradient(180deg,#fffdf5_0%,#fff7df_100%)] shadow-[0_0_0_1px_rgba(196,134,31,0.08)]'
      : 'border-[#f1e9e1] bg-[linear-gradient(180deg,#ffffff_0%,var(--theme-app-bg)_100%)]';

  return (
    <div className={`rounded-[12px] border px-3 py-2.5 ${toneClassName}`}>
      <span className="block text-[0.68rem] uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
        {label}
      </span>
      <strong className="mt-1 block text-[0.88rem] font-medium text-[var(--theme-text)]">
        {value}
      </strong>
      {detail ? (
        <span className="mt-0.5 block text-[0.68rem] font-medium text-[var(--theme-text-muted)]">
          {detail}
        </span>
      ) : null}
    </div>
  );
}

function FinalReviewPanel({
  purchaseOrder,
  receivingEditValues,
  isReceivingSaving,
  error,
  onReceivingChange,
  canComplete,
  hasPaidBalancing = false,
  hasSavedBalancing = false,
  isSaving,
  onFinalizeForBalancing,
  onReopenReceiving,
  onComplete,
}) {
  const receivingItems = Array.isArray(receivingEditValues) ? receivingEditValues : [];
  const receivingUnit = purchaseOrder.stockUnit === 'kg' ? 'kg' : 'sacks';
  const dryItem = receivingItems.find((item) => item.category === 'Dry');
  const wetItem = receivingItems.find((item) => item.category === 'Wet');
  const rejectItem = receivingItems.find((item) => item.category === 'Reject');
  const primaryItem = dryItem || wetItem || receivingItems[0];
  const receiptImageItem = receivingItems.find((item) => item.image) || null;
  const receiptImagePath = receivingItems.map((item) => item.image).find(Boolean) || '';
  const [countdownNow, setCountdownNow] = useState(() => new Date());
  const receiptImageRetention = getReceiptImageRetention(receiptImageItem, countdownNow);
  const receiptImageRetentionDays =
    Number(receivingItems.map((item) => item.imageRetentionDays).find(Boolean)) || 30;
  const receiptImageRetentionDuration = formatRetentionDuration(receiptImageRetentionDays);
  const receivedAt = primaryItem?.receivedAt;
  const remarksParts = splitRemarksAndReceiptDetails(primaryItem?.remarks || '');
  const receiptFields = parseReceiptDetails(remarksParts.receiptDetails);
  const receiptNetKg = getReceiptNumericValue(receiptFields, 'Net KG');
  const storedNetKg = getTotalNetKg(receivingItems, 0);
  const storedRawNetKg = getTotalRawNetKg(receivingItems);
  const rawNetKg = receiptNetKg || storedRawNetKg || storedNetKg;
  const { totalQuantity, totalEnteredQuantity } = getReceivingTotals(receivingItems, receivingUnit);
  const actualReceivedSummary = getActualReceivedSummary(totalQuantity, receivingUnit);
  const requestedQuantity = Number(purchaseOrder.receivedQuantity ?? purchaseOrder.quantity);
  const hasUnderReceivedWarning =
    canComplete &&
    Number.isFinite(requestedQuantity) &&
    requestedQuantity > 0 &&
    totalQuantity > 0 &&
    totalQuantity < requestedQuantity;
  const missingReceivedQuantityError =
    canComplete && totalEnteredQuantity <= 0 ? 'Enter a dry, wet, or reject quantity greater than 0.' : '';
  const overReceivedWarning =
    canComplete &&
    Number.isFinite(requestedQuantity) &&
    requestedQuantity > 0 &&
    totalQuantity > requestedQuantity
      ? `Actual received is ${formatQuantityForWarning(totalQuantity, receivingUnit)}, ${formatQuantityForWarning(
          totalQuantity - requestedQuantity,
          receivingUnit
        )} over the Step 2 quantity (${formatQuantityForWarning(requestedQuantity, receivingUnit)}). Finalizing will approve this overage for balancing.`
      : '';
  const displayedError = missingReceivedQuantityError || (canComplete ? error : '');
  const actualReceivedTone = missingReceivedQuantityError
    ? 'error'
    : hasUnderReceivedWarning || overReceivedWarning
      ? 'warning'
      : 'neutral';
  const isCompleted = purchaseOrder.status === 'Completed';
  const isReadyForBalancing = Boolean(purchaseOrder.balancingReadyAt);
  const isConfirmBlockedByBalancing = canComplete && isReadyForBalancing && !hasPaidBalancing;
  const areReceivingFieldsLocked = isCompleted || isReadyForBalancing || isReceivingSaving;
  const canReopenReceiving = canComplete && isReadyForBalancing && !hasSavedBalancing;
  const [previewImage, setPreviewImage] = useState(null);
  const [grossKgDraft, setGrossKgDraft] = useState(() =>
    formatEditableNumber(rawNetKg, { maximumFractionDigits: 2, showZero: true })
  );
  const [isGrossKgEditing, setIsGrossKgEditing] = useState(false);

  useEffect(() => {
    if (!receiptImageItem?.imageExpiresAt) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setCountdownNow(new Date());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [receiptImageItem?.imageExpiresAt]);

  useEffect(() => {
    if (!isGrossKgEditing) {
      setGrossKgDraft(formatEditableNumber(rawNetKg, { maximumFractionDigits: 2, showZero: true }));
    }
  }, [isGrossKgEditing, rawNetKg]);

  function updateAllItems(fieldName, value) {
    const normalizedValue = ['quantity', 'unitCost'].includes(fieldName)
      ? normalizeZeroNumberInput(value)
      : value;

    receivingItems.forEach((item) => {
      onReceivingChange(item.id, fieldName, normalizedValue);
    });
  }

  function updateMoistureContent(value) {
    updateAllItems('moistureContent', normalizeWholeNumberInput(value));

    if (receivingUnit !== 'sacks') {
      return;
    }

    updateQuantityKgAllocation(rawNetKg);
  }

  function updateTotalNetKg(value) {
    if (receivingUnit !== 'sacks') {
      return;
    }

    updateQuantityKgAllocation(value);
    updateReceiptDetails(value);
  }

  function handleGrossKgChange(event) {
    const nextValue = sanitizeFormattedNumber(event.target.value);

    setGrossKgDraft(nextValue);
    updateTotalNetKg(nextValue);
  }

  function handleGrossKgBlur() {
    if (!grossKgDraft) {
      setGrossKgDraft('0');
      updateTotalNetKg('0');
    }

    setIsGrossKgEditing(false);
  }

  function updateQuantityKgAllocation(value) {
    const normalizedValue = sanitizeFormattedNumber(value);
    const numericValue = Number(normalizedValue);
    const grossKg = Number.isFinite(numericValue) && normalizedValue ? numericValue : 0;
    const allocatedItems = allocateGrossKgByQuantity(receivingItems, grossKg);

    receivingItems.forEach((item) => {
      if (item.category === 'Reject') {
        const rejectNetKg = parseFormattedNumber(item.quantityKg || item.rawNetKg);

        onReceivingChange(item.id, 'quantityKg', rejectNetKg > 0 ? String(rejectNetKg) : '');
        onReceivingChange(item.id, 'rawNetKg', rejectNetKg > 0 ? String(rejectNetKg) : '');
        onReceivingChange(item.id, 'mcDeductionKg', '');
        onReceivingChange(item.id, 'adjustedNetKg', '');
        return;
      }

      const allocatedNetKg = allocatedItems.get(item.id);

      onReceivingChange(item.id, 'quantityKg', allocatedNetKg === '' ? '' : String(allocatedNetKg));
      onReceivingChange(item.id, 'rawNetKg', allocatedNetKg === '' ? '' : String(allocatedNetKg));
      onReceivingChange(item.id, 'mcDeductionKg', '');
      onReceivingChange(item.id, 'adjustedNetKg', '');
    });
  }

  function updateReceiptDetails(value) {
    const normalizedValue = sanitizeFormattedNumber(value);
    const numericValue = Number(normalizedValue);
    const nextDetails = buildGrossReceiptDetails(
      remarksParts.receiptDetails,
      Number.isFinite(numericValue) && normalizedValue ? numericValue : 0
    );

    updateAllItems('remarks', mergeRemarksAndReceiptDetails(remarksParts.remarks, nextDetails));
  }

  if (!receivingItems.length) {
    return (
      <div className="rounded-[16px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-soft)] px-4 py-5 text-[0.9rem] text-[var(--theme-text-muted)]">
        Waiting for staff receiving details.
      </div>
    );
  }

  return (
    <div className="flex min-h-[420px] flex-col gap-3">
      <div className="grid gap-2">
        <CompactSummary
          label="Actual Received"
          value={actualReceivedSummary.value}
          detail={actualReceivedSummary.detail}
          tone={actualReceivedTone}
        />
      </div>

      <div className="grid gap-2 rounded-[14px] border border-[#f1e9e1] bg-white px-3 py-2.5 text-[0.84rem] text-[var(--theme-text-subtle)]">
        <div className="flex items-center justify-between gap-3">
          <strong className="text-[0.9rem] text-[var(--theme-text)]">Receiving Details</strong>
          <span className="text-[0.76rem] text-[var(--theme-text-muted)]">{formatDate(receivedAt)}</span>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <MinimalInput
            aria-label="Dry quantity kg"
            inputMode="numeric"
            value={formatEditableNumber(dryItem?.quantity, { maximumFractionDigits: 0, showZero: true })}
            onChange={(event) =>
              dryItem && onReceivingChange(dryItem.id, 'quantity', normalizeZeroNumberInput(event.target.value))
            }
            disabled={areReceivingFieldsLocked || !dryItem}
            prefix="Dry"
            suffix={receivingUnit}
          />
          <MinimalInput
            aria-label="Wet quantity kg"
            inputMode="numeric"
            value={formatEditableNumber(wetItem?.quantity, { maximumFractionDigits: 0, showZero: true })}
            onChange={(event) =>
              wetItem && onReceivingChange(wetItem.id, 'quantity', normalizeZeroNumberInput(event.target.value))
            }
            disabled={areReceivingFieldsLocked || !wetItem}
            prefix="Wet"
            suffix={receivingUnit}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <MinimalInput
            aria-label="Reject net KG"
            inputMode="decimal"
            value={formatEditableNumber(
              receivingUnit === 'sacks' ? rejectItem?.quantityKg : rejectItem?.quantity,
              { maximumFractionDigits: 2, showZero: true }
            )}
            onChange={(event) => {
              if (!rejectItem) {
                return;
              }

              const value = normalizeZeroNumberInput(event.target.value);

              if (receivingUnit === 'sacks') {
                onReceivingChange(rejectItem.id, 'quantityKg', value);
                onReceivingChange(rejectItem.id, 'rawNetKg', value);
                return;
              }

              onReceivingChange(rejectItem.id, 'quantity', value);
            }}
            disabled={areReceivingFieldsLocked || !rejectItem}
            prefix="Reject"
            suffix={receivingUnit === 'sacks' ? 'kg' : receivingUnit}
          />
          <MinimalInput
            aria-label="Moisture content"
            inputMode="numeric"
            pattern="[0-9]*"
            value={formatEditableNumber(stripPercentValue(primaryItem?.moistureContent), {
              maximumFractionDigits: 0,
              showZero: true,
            })}
            onChange={(event) => updateMoistureContent(event.target.value)}
            disabled={areReceivingFieldsLocked}
            prefix="MC"
            suffix="%"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <MinimalInput
            aria-label="Unit cost"
            inputMode="decimal"
            value={formatEditableNumber(primaryItem?.unitCost, { maximumFractionDigits: 2, showZero: true })}
            onChange={(event) => updateAllItems('unitCost', event.target.value)}
            disabled={areReceivingFieldsLocked}
            prefix="₱"
          />
          {receivingUnit === 'sacks' ? (
            <MinimalInput
              aria-label="Total gross KG"
              inputMode="decimal"
              value={
                isGrossKgEditing
                  ? grossKgDraft
                  : formatEditableNumber(rawNetKg, { maximumFractionDigits: 2, showZero: true })
              }
              onBlur={handleGrossKgBlur}
              onChange={handleGrossKgChange}
              onFocus={() => {
                setGrossKgDraft(formatEditableNumber(rawNetKg, { maximumFractionDigits: 2, showZero: true }));
                setIsGrossKgEditing(true);
              }}
              disabled={areReceivingFieldsLocked}
              prefix="Gross KG"
              suffix="kg"
            />
          ) : (
            <span aria-hidden="true" />
          )}
        </div>

        <textarea
          className="min-h-[46px] max-h-[76px] rounded-[12px] border border-[var(--theme-border)] bg-[var(--theme-surface-warm)] px-3 py-2 text-[0.82rem] outline-none transition placeholder:text-[#b7aea6] focus:border-[var(--theme-text-subtle)]"
          value={remarksParts.remarks}
          onChange={(event) =>
            updateAllItems(
              'remarks',
              mergeRemarksAndReceiptDetails(event.target.value, remarksParts.receiptDetails)
            )
          }
          disabled={areReceivingFieldsLocked}
          placeholder="Remarks"
        />

        {remarksParts.receiptDetails || receiptImagePath ? (
          <details className="rounded-[12px] border border-[#f1e9e1] bg-[var(--theme-app-bg)] px-3 py-2">
            <summary className="cursor-pointer select-none text-[0.78rem] font-semibold text-[var(--theme-text-subtle)]">
              Receipt Details
            </summary>
            {receiptImagePath ? (
              <button
                className="mt-2 inline-flex max-w-full text-[0.76rem] font-semibold text-[#4f7b52] underline-offset-2 hover:underline"
                type="button"
                onClick={() =>
                  setPreviewImage({
                    url: buildAssetUrl(receiptImagePath),
                    title: formatPurchaseOrderCode(purchaseOrder),
                    details: remarksParts.receiptDetails,
                    remarks: remarksParts.remarks,
                    retention: receiptImageRetention,
                    item: {
                      ...receiptImageItem,
                      quantity: totalQuantity,
                      quantityUnit: receivingUnit,
                    },
                  })
                }
            >
              <span className="truncate">{getReceiptImageFileName(receiptImagePath)}</span>
            </button>
            ) : remarksParts.receiptDetails ? (
              <span className="mt-2 inline-flex max-w-full rounded-full border border-[#ead7a8] bg-[#fff8e6] px-2.5 py-1 text-[0.72rem] font-semibold text-[#8a6424]">
                Receipt image self-destructed after {receiptImageRetentionDuration}
              </span>
            ) : null}
            {remarksParts.receiptDetails ? (
              <pre className="m-0 mt-2 max-h-28 overflow-y-auto whitespace-pre-wrap text-[0.76rem] leading-5 text-[var(--theme-text-muted)]">
                {remarksParts.receiptDetails}
              </pre>
            ) : null}
          </details>
        ) : null}
      </div>

      <div className="mt-auto grid gap-2">
        {hasUnderReceivedWarning ? (
          <InlineWarningMessage>
            Actual received is below the Step 2 requested quantity (
            {formatQuantityForWarning(requestedQuantity, receivingUnit)}).
          </InlineWarningMessage>
        ) : null}
        {overReceivedWarning ? (
          <InlineWarningMessage>{overReceivedWarning}</InlineWarningMessage>
        ) : null}
        {displayedError ? <InlineErrorMessage>{displayedError}</InlineErrorMessage> : null}
        <p className="m-0 rounded-[12px] border border-[#f1e9e1] bg-[var(--theme-app-bg)] px-3 py-2 text-[0.8rem] leading-5 text-[var(--theme-text-muted)]">
          {isCompleted
            ? 'This purchase order has already been completed.'
            : canComplete
            ? isReadyForBalancing
              ? hasPaidBalancing
                ? 'Supplier balancing is paid. You can now confirm completed.'
                : 'Receiving is finalized. Mark this P.O. balancing as Paid before confirming completed.'
              : 'Finalize receiving before preparing Supplier Balancing.'
            : 'Admin can confirm completed after staff submits receiving.'}
        </p>
        {!isCompleted ? (
          <>
            {canComplete && !isReadyForBalancing ? (
              <button
                className="w-full rounded-full border border-[var(--theme-accent)] bg-white px-5 py-3 text-[0.9rem] font-semibold text-[var(--theme-accent-strong)] shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.08)] transition hover:bg-[var(--theme-accent-soft)] disabled:cursor-not-allowed disabled:border-[var(--theme-border)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)] disabled:opacity-70 disabled:shadow-none"
                type="button"
                data-progress-action="true"
                onClick={onFinalizeForBalancing}
                disabled={
                  isSaving ||
                  isReceivingSaving ||
                  !receivingItems.length ||
                  Boolean(missingReceivedQuantityError)
                }
              >
                {isSaving || isReceivingSaving ? 'Saving...' : 'Finalize for Balancing'}
              </button>
            ) : null}
            {canReopenReceiving ? (
              <button
                className="w-full rounded-full border border-[var(--theme-accent)] bg-white px-5 py-3 text-[0.9rem] font-semibold text-[var(--theme-accent-strong)] shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.08)] transition hover:bg-[var(--theme-accent-soft)] disabled:cursor-not-allowed disabled:border-[var(--theme-border)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)] disabled:opacity-70 disabled:shadow-none"
                type="button"
                data-progress-action="true"
                onClick={onReopenReceiving}
                disabled={isSaving || isReceivingSaving}
              >
                {isSaving || isReceivingSaving ? 'Reopening...' : 'Reopen Step 3'}
              </button>
            ) : null}
            <button
              className="w-full rounded-full bg-[var(--theme-accent)] px-5 py-3 text-[0.94rem] font-semibold text-white shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.18)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:border disabled:border-[var(--theme-border)] disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)] disabled:opacity-60 disabled:shadow-none"
              type="button"
              data-progress-action="true"
              onClick={onComplete}
              disabled={
                isSaving ||
                isReceivingSaving ||
                !canComplete ||
                !isReadyForBalancing ||
                !hasPaidBalancing ||
                !receivingItems.length ||
                Boolean(missingReceivedQuantityError)
              }
              title={isConfirmBlockedByBalancing ? 'Mark this P.O. balancing as Paid first.' : undefined}
            >
              {(isSaving || isReceivingSaving) && canComplete ? 'Saving...' : 'Confirm Completed'}
            </button>
          </>
        ) : null}
      </div>
      {previewImage ? (
        <ReceiptImagePreviewModal
          image={previewImage}
          onClose={() => setPreviewImage(null)}
        />
      ) : null}
    </div>
  );
}

function InlineErrorMessage({ children }) {
  return (
    <p className="m-0 rounded-[12px] border border-[#efc3b8] bg-[#fff6f3] px-3 py-2 text-[0.8rem] font-medium leading-5 text-[#a34334]">
      {children}
    </p>
  );
}

function InlineWarningMessage({ children }) {
  return (
    <p className="m-0 rounded-[12px] border border-[#ead7a8] bg-[#fff8e6] px-3 py-2 text-[0.8rem] font-medium leading-5 text-[#8a6424]">
      {children}
    </p>
  );
}

function ReceiptImagePreviewModal({ image, onClose }) {
  const [zoom, setZoom] = useState(1);
  const [now, setNow] = useState(() => new Date());
  const receiptFields = parseReceiptDetails(image.details);
  const retention = image.item
    ? getReceiptImageRetention(image.item, now)
    : image.retention || {};

  useEffect(() => {
    if (!image.item?.imageExpiresAt) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [image.item?.imageExpiresAt]);

  function handleZoomOut() {
    setZoom((currentZoom) => Math.max(0.5, Math.round((currentZoom - 0.25) * 100) / 100));
  }

  function handleZoomIn() {
    setZoom((currentZoom) => Math.min(3, Math.round((currentZoom + 0.25) * 100) / 100));
  }

  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.55)]"
      contentClassName="!px-4"
      zIndexClassName="z-[70]"
    >
      <div className="mx-auto w-full max-w-5xl rounded-[22px] border border-[#e8ded3] bg-white p-3 shadow-[0_24px_70px_rgba(var(--theme-shadow-rgb),0.32)]">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3 px-1">
          <strong className="text-[0.95rem] text-[var(--theme-text)]">
            {image.title || 'Receipt image'}
          </strong>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center overflow-hidden rounded-full border border-[#e3d9cf] bg-white">
              <button
                className="inline-flex h-9 w-9 items-center justify-center text-[#73675d] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:text-[#c5bbb0]"
                type="button"
                onClick={handleZoomOut}
                disabled={zoom <= 0.5}
                aria-label="Zoom out receipt image"
                title="Zoom out"
              >
                <Minus className="h-4 w-4" aria-hidden="true" />
              </button>
              <span className="min-w-[54px] border-x border-[var(--theme-border-soft)] px-2 text-center text-[0.78rem] font-semibold text-[var(--theme-text-subtle)]">
                {Math.round(zoom * 100)}%
              </span>
              <button
                className="inline-flex h-9 w-9 items-center justify-center text-[#73675d] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:text-[#c5bbb0]"
                type="button"
                onClick={handleZoomIn}
                disabled={zoom >= 3}
                aria-label="Zoom in receipt image"
                title="Zoom in"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <button
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#e3d9cf] bg-white text-[#73675d] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              onClick={onClose}
              aria-label="Close receipt image preview"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="grid max-h-[78vh] gap-3 overflow-y-auto">
          {image.url ? (
            <div className="relative max-h-[58vh] overflow-auto rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] p-2">
              {retention.label ? (
                <span
                  className={`sticky right-3 top-3 z-10 ml-auto flex w-max items-center rounded-full border px-3 py-1.5 text-[0.76rem] font-semibold shadow-[0_8px_22px_rgba(var(--theme-shadow-rgb),0.12)] ${
                    retention.tone === 'danger'
                      ? 'border-[#efc3b8] bg-[#fff6f3] text-[#a34334]'
                      : retention.tone === 'warning'
                        ? 'border-[#ead7a8] bg-[#fff8e6] text-[#8a6424]'
                        : 'border-[#d6e6d7] bg-[#f4faf4] text-[#3f7545]'
                  }`}
                >
                  {retention.modalLabel || retention.label}
                </span>
              ) : null}
              <img
                className="mx-auto block h-auto max-w-none rounded-[12px] object-contain"
                src={image.url}
                alt={`${image.title || 'Receipt'} full preview`}
                style={{ width: `${zoom * 100}%` }}
              />
            </div>
          ) : null}
          <div className="rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3">
            <strong className="block text-[0.86rem] text-[var(--theme-text-strong)]">
              Receipt Details
            </strong>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-white p-3">
                <strong className="block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
                  Captured Fields
                </strong>
                <div className="mt-2 grid gap-1.5 text-[0.8rem] text-[var(--theme-text-subtle)]">
                  {receiptFields.length ? (
                    receiptFields.map((field) => (
                      <div
                        key={`${field.label}-${field.value}`}
                        className="grid grid-cols-[112px_minmax(0,1fr)] gap-2"
                      >
                        <span className="font-semibold text-[var(--theme-text-muted)]">{field.label}</span>
                        <span className="break-words font-medium text-[var(--theme-text-strong)]">
                          {field.value || '-'}
                        </span>
                      </div>
                    ))
                  ) : (
                    <pre className="m-0 max-h-56 overflow-y-auto whitespace-pre-wrap text-[0.8rem] leading-5 text-[var(--theme-text-subtle)]">
                      {image.details || '-'}
                    </pre>
                  )}
                </div>
              </div>

              <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-white p-3">
                <strong className="block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
                  Remarks
                </strong>
                <p className="m-0 mt-2 whitespace-pre-wrap text-[0.84rem] leading-5 text-[var(--theme-text-subtle)]">
                  {image.remarks || '-'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function getReceivingTotals(items, unit = 'kg') {
  return items.reduce(
    (totals, item) => {
      const isRejectSacksRow =
        unit === 'sacks' && String(item.category || '').trim() === 'Reject';
      const quantity = parseFormattedNumber(item.quantity);
      const rejectNetKg = parseFormattedNumber(item.quantityKg || item.rawNetKg);

      return {
        totalQuantity: totals.totalQuantity + (isRejectSacksRow ? 0 : quantity),
        totalEnteredQuantity:
          totals.totalEnteredQuantity + (isRejectSacksRow ? rejectNetKg : quantity),
      };
    },
    { totalQuantity: 0, totalEnteredQuantity: 0 }
  );
}

function getTotalNetKg(items, fallbackNetKg = 0) {
  const totalNetKg = items.reduce(
    (total, item) =>
      item.quantityUnit === 'sacks' && String(item.category || '').trim() !== 'Reject'
        ? total + parseFormattedNumber(item.quantityKg)
        : total,
    0
  );

  return totalNetKg > 0 ? totalNetKg : parseFormattedNumber(fallbackNetKg);
}

function getTotalRawNetKg(items) {
  return roundKg(
    items.reduce(
      (total, item) =>
        item.quantityUnit === 'sacks' && String(item.category || '').trim() !== 'Reject'
          ? total + parseFormattedNumber(item.rawNetKg)
          : total,
      0
    )
  );
}

function getActualReceivedSummary(quantity, unit) {
  const formattedQuantity = `${formatCompactNumber(quantity)} ${unit}`;

  return {
    value: formattedQuantity,
    detail: unit === 'sacks' ? 'Total Received Sacks' : '',
  };
}

function buildGrossReceiptDetails(receiptDetails, grossKg) {
  const cleanedDetails = removeReceiptDetailLines(receiptDetails, [
    'Net KG',
    'MC Deduction KG',
    'Adjusted Net KG',
  ]);

  if (!Number(grossKg || 0)) {
    return cleanedDetails;
  }

  return upsertReceiptDetailLine(cleanedDetails, 'Net KG', `${formatDecimal(grossKg)} kg`);
}

function upsertReceiptDetailLine(receiptDetails, label, value) {
  const lines = String(receiptDetails || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const lineIndex = lines.findIndex((line) => {
    const separatorIndex = line.indexOf(':');

    return (
      separatorIndex !== -1 &&
      line.slice(0, separatorIndex).trim().toLowerCase() === label.toLowerCase()
    );
  });
  const nextLine = `${label}: ${value}`;

  if (lineIndex === -1) {
    return [...lines, nextLine].join('\n');
  }

  return lines
    .map((line, index) => (index === lineIndex ? nextLine : line))
    .join('\n');
}

function removeReceiptDetailLines(receiptDetails, labels) {
  const normalizedLabels = new Set(labels.map((label) => label.toLowerCase()));

  return String(receiptDetails || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => {
      const separatorIndex = line.indexOf(':');

      if (separatorIndex === -1) {
        return true;
      }

      return !normalizedLabels.has(line.slice(0, separatorIndex).trim().toLowerCase());
    })
    .join('\n');
}

function roundKg(value) {
  return Math.round(parseFormattedNumber(value) * 100) / 100;
}

function allocateGrossKgByQuantity(items = [], grossKg = 0) {
  const allocatableItems = items.filter(
    (item) =>
      item.quantityUnit === 'sacks' &&
      String(item.category || '').trim() !== 'Reject' &&
      parseFormattedNumber(item.quantity) > 0
  );
  const totalQuantity = allocatableItems.reduce(
    (total, item) => total + parseFormattedNumber(item.quantity),
    0
  );
  const normalizedGrossKg = parseFormattedNumber(grossKg);

  if (normalizedGrossKg <= 0 || totalQuantity <= 0) {
    return new Map(allocatableItems.map((item) => [item.id, '']));
  }

  let allocatedTotal = 0;

  return allocatableItems.reduce((allocations, item, index) => {
    const isLastItem = index === allocatableItems.length - 1;
    const allocatedKg = isLastItem
      ? roundKg(normalizedGrossKg - allocatedTotal)
      : roundKg((parseFormattedNumber(item.quantity) / totalQuantity) * normalizedGrossKg);

    allocatedTotal = roundKg(allocatedTotal + allocatedKg);
    allocations.set(item.id, allocatedKg);

    return allocations;
  }, new Map());
}

function splitRemarksAndReceiptDetails(value) {
  const lines = String(value || '').split('\n');
  const firstReceiptLineIndex = lines.findIndex(isReceiptDetailLine);

  if (firstReceiptLineIndex === -1) {
    return {
      remarks: String(value || '').trim(),
      receiptDetails: '',
    };
  }

  return {
    remarks: lines.slice(0, firstReceiptLineIndex).join('\n').trim(),
    receiptDetails: lines.slice(firstReceiptLineIndex).join('\n').trim(),
  };
}

function mergeRemarksAndReceiptDetails(remarks, receiptDetails) {
  return [remarks.trim(), receiptDetails.trim()].filter(Boolean).join('\n\n');
}

function parseReceiptDetails(value) {
  return String(value || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const separatorIndex = line.indexOf(':');

      if (separatorIndex === -1) {
        return null;
      }

      return {
        label: line.slice(0, separatorIndex).trim(),
        value: line.slice(separatorIndex + 1).trim(),
      };
    })
    .filter(Boolean);
}

function getReceiptNumericValue(fields, label) {
  const field = fields.find(
    (item) => item.label.toLowerCase() === label.toLowerCase()
  );

  if (!field) {
    return 0;
  }

  return Number(String(field.value || '').replace(/[^\d.]/g, '')) || 0;
}

function isReceiptDetailLine(line) {
  return /^(P\.O\. Code|Ticket No\.|Customer|Commodity|Plate #|Driver|Weigher|Inbound Wt kg|In Bound Wt kg|Inbound Date|In Bound Date|Outbound Wt kg|Outbound|Outbound Date|Gross KG|Tare KG|Net KG|MC Deduction KG|Adjusted Net KG):/.test(
    line.trim()
  );
}

function isProgressActionElement(target) {
  return target instanceof Element && Boolean(target.closest('[data-progress-action="true"]'));
}

function isEditorToggleElement(target) {
  return target instanceof Element && Boolean(target.closest('[data-progress-editor-toggle="true"]'));
}

function isCustomSelectDropdownElement(target) {
  return target instanceof Element && Boolean(target.closest('[data-custom-select-dropdown="true"]'));
}

function MinimalInput({ prefix, suffix, ...props }) {
  const inputType = props.type || 'text';

  return (
    <label className="flex min-h-[36px] items-center gap-2 rounded-[12px] border border-[var(--theme-border)] bg-white px-3 py-2 transition focus-within:border-[var(--theme-text-subtle)]">
      {prefix ? (
        <span className="shrink-0 text-[0.7rem] font-semibold text-[var(--theme-text-muted)]">
          {prefix}
        </span>
      ) : null}
      <input
        className={`min-w-0 flex-1 border-0 bg-transparent p-0 text-right text-[0.82rem] outline-none ${
          inputType === 'number'
            ? '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
            : ''
        }`}
        {...props}
      />
      {suffix ? (
        <span className="shrink-0 text-[0.72rem] text-[var(--theme-text-muted)]">
          {suffix}
        </span>
      ) : null}
    </label>
  );
}

function stripPercentValue(value) {
  return String(value || '').replace(/%/g, '').trim();
}

function Message({ children, tone }) {
  const className =
    tone === 'success'
      ? 'border-[#e6ecdf] bg-[#f5f8f1] text-[#55714a]'
      : 'border-[#f0ddd8] bg-[#fbf3f1] text-[#9a5649]';

  return (
    <p className={`m-0 mt-5 rounded-[16px] border px-4 py-3 text-[0.9rem] ${className}`}>
      {children}
    </p>
  );
}

function formatCompactNumber(value) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatDecimal(value) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatEditableNumber(value, options = {}) {
  const normalizedValue = sanitizeFormattedNumber(value);

  if (!normalizedValue && options.showZero) {
    return '0';
  }

  if (normalizedValue.includes('.')) {
    const [integerPart, decimalPart = ''] = normalizedValue.split('.');
    const formattedInteger = integerPart
      ? new Intl.NumberFormat('en-US', {
          maximumFractionDigits: 0,
        }).format(Number(integerPart))
      : '';
    const maxDecimals = options.maximumFractionDigits ?? 2;

    return `${formattedInteger}.${decimalPart.slice(0, maxDecimals)}`;
  }

  const numericValue = Number(normalizedValue);

  if (!numericValue) {
    return options.showZero ? '0' : '';
  }

  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  }).format(numericValue);
}

function normalizeZeroNumberInput(value) {
  return sanitizeFormattedNumber(value) || '0';
}

function normalizeWholeNumberInput(value) {
  const normalizedValue = sanitizeFormattedNumber(value).replace(/\D/g, '');

  if (!normalizedValue) {
    return '0';
  }

  return normalizedValue.replace(/^0+(?=\d)/, '') || '0';
}

function sanitizeFormattedNumber(value) {
  const normalizedValue = String(value || '').replace(/,/g, '').trim();

  if (!normalizedValue) {
    return '';
  }

  const [integerPart, ...decimalParts] = normalizedValue.replace(/[^\d.]/g, '').split('.');
  const decimalPart = decimalParts.join('');

  return decimalParts.length ? `${integerPart}.${decimalPart}` : integerPart;
}

function parseFormattedNumber(value) {
  const normalizedValue = sanitizeFormattedNumber(value);

  return normalizedValue ? Number(normalizedValue) || 0 : 0;
}

function formatDate(value) {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatSupplierDisplay(sacksMark, supplierName) {
  const normalizedSacksMark = String(sacksMark || '').trim();
  const normalizedSupplierName = String(supplierName || '').trim();

  if (normalizedSacksMark && normalizedSupplierName) {
    return `${normalizedSacksMark} - ${normalizedSupplierName}`;
  }

  return normalizedSacksMark || normalizedSupplierName || '-';
}

function getReceiptImageFileName(imagePath) {
  return String(imagePath || '').split('/').filter(Boolean).pop() || 'Receipt image';
}

function getReceiptImageRetention(item, now = new Date()) {
  const retentionDays = Number(item?.imageRetentionDays || 30);
  const daysRemaining = Number(item?.imageDaysRemaining);
  const expiresAt = item?.imageExpiresAt ? new Date(item.imageExpiresAt) : null;
  const remainingMs =
    expiresAt && !Number.isNaN(expiresAt.getTime())
      ? expiresAt.getTime() - now.getTime()
      : null;

  if (!item?.image || (!Number.isFinite(daysRemaining) && remainingMs === null)) {
    return {
      daysRemaining: null,
      retentionDays,
      label: '',
      tone: 'neutral',
    };
  }

  const normalizedDaysRemaining =
    remainingMs === null
      ? Math.max(0, Math.min(retentionDays, daysRemaining))
      : Math.max(0, Math.min(retentionDays, Math.ceil(remainingMs / MS_PER_DAY)));
  const countdownLabel = formatSelfDestructCountdown(remainingMs);

  return {
    daysRemaining: normalizedDaysRemaining,
    retentionDays,
    label:
      countdownLabel ||
      (normalizedDaysRemaining <= 0
        ? 'Deletes today'
        : `${normalizedDaysRemaining} day${normalizedDaysRemaining === 1 ? '' : 's'} left`),
    modalLabel:
      countdownLabel
        ? remainingMs <= 0
          ? countdownLabel
          : `${countdownLabel} to self-destruct`
        : normalizedDaysRemaining <= 0
          ? 'Self-destructs today'
          : `${normalizedDaysRemaining} day${normalizedDaysRemaining === 1 ? '' : 's'} left to self-destruct`,
    tone:
      normalizedDaysRemaining <= 3
        ? 'danger'
        : normalizedDaysRemaining <= 7
          ? 'warning'
          : 'success',
  };
}

function formatSelfDestructCountdown(remainingMs) {
  if (!Number.isFinite(remainingMs)) {
    return '';
  }

  if (remainingMs <= 0) {
    return 'Self-destructing now';
  }

  if (remainingMs >= MS_PER_DAY) {
    return '';
  }

  const totalSeconds = Math.ceil(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];

  if (hours) {
    parts.push(`${hours}h`);
  }

  if (hours || minutes) {
    parts.push(`${minutes}m`);
  }

  parts.push(`${seconds}s`);

  return `${parts.join(' ')} left`;
}

function formatRetentionDuration(retentionDays) {
  const retentionMs = Number(retentionDays || 0) * MS_PER_DAY;

  if (!Number.isFinite(retentionMs) || retentionMs <= 0) {
    return `${retentionDays || 0} days`;
  }

  if (retentionMs < 60 * 60 * 1000) {
    const minutes = Math.max(1, Math.round(retentionMs / (60 * 1000)));
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  if (retentionMs < MS_PER_DAY) {
    const hours = Math.max(1, Math.round(retentionMs / (60 * 60 * 1000)));
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }

  return `${retentionDays} day${Number(retentionDays) === 1 ? '' : 's'}`;
}

function formatQuantityForWarning(quantity, stockUnit) {
  return `${Number(quantity || 0).toLocaleString('en-US', {
    maximumFractionDigits: stockUnit === 'kg' ? 2 : 0,
  })} ${stockUnit === 'kg' ? 'kg' : 'sacks'}`;
}

function formatPurchaseOrderCode(purchaseOrder) {
  return purchaseOrder.poCode || `PO-${String(purchaseOrder.id).padStart(4, '0')}`;
}

function focusEditableField(fieldRef) {
  window.requestAnimationFrame(() => {
    fieldRef.current?.focus?.();
    fieldRef.current?.select?.();
  });
}

function openEditableSelect(fieldRef) {
  window.requestAnimationFrame(() => {
    fieldRef.current?.focus?.();
    fieldRef.current?.click?.();
  });
}

function hasChangedReceivingValues(currentItems = [], initialItems = []) {
  if (currentItems.length !== initialItems.length) {
    return true;
  }

  const initialItemsById = new Map(initialItems.map((item) => [String(item.id), item]));
  const trackedFields = [
    'category',
    'moistureContent',
    'quantity',
    'quantityUnit',
    'quantityKg',
    'rawNetKg',
    'mcPercent',
    'mcDeductionKg',
    'adjustedNetKg',
    'unitCost',
    'remarks',
  ];

  return currentItems.some((currentItem) => {
    const initialItem = initialItemsById.get(String(currentItem.id));

    if (!initialItem) {
      return true;
    }

    return trackedFields.some(
      (fieldName) =>
        normalizeReceivingCompareValue(currentItem[fieldName]) !==
        normalizeReceivingCompareValue(initialItem[fieldName])
    );
  });
}

function normalizeReceivingCompareValue(value) {
  return String(value ?? '').trim();
}

function getInitialStageTwoValues(purchaseOrder) {
  return {
    actualPrice: String(purchaseOrder.actualPrice ?? purchaseOrder.price ?? ''),
    receivedQuantity: String(purchaseOrder.receivedQuantity ?? purchaseOrder.quantity),
    lansaName: purchaseOrder.lansaName || '',
    warehouseLocation: purchaseOrder.warehouseLocation || '',
    deliveryDate: purchaseOrder.deliveryDate || '',
  };
}

export default PurchaseOrderProgressModal;
